
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import {
  SortableColumn,
  SortChangeEvent,
  SortOption,
  ComponentMode,
  ViewModeDisplay,
  SortState
} from '@/types/sort';
import {
  getDefaultDirection,
  convertArraysToSortOptions,
  convertSortOptionsToArrays,
  getAvailableSecondaryColumns,
  validateSortState,
  hasActiveSorting,
  getEmptySortState,
  debounce,
  createViewModeDisplay,
  toggleSortDirection,
  createTempSortState,
  getColumnDisplayName,
  cleanColumnName
} from '@/utils/sortUtils';

@Component({
  name: 'ReportSort'
})
export default class ReportSort extends Vue {
  @Prop({ required: true })
  public sortableColumns!: SortableColumn[];

  @Prop({ default: () => [] })
  public initialSortBy!: string[];

  @Prop({ default: () => [] })
  public initialSortDesc!: boolean[];

  // Mode management state
  public isEditMode: boolean = false;
  public hasUnsavedChanges: boolean = false;

  // Current sort state (what's actually applied)
  public currentState: SortState = {
    primarySort: { column: '', direction: 'asc' },
    secondarySort: null,
    showSecondarySort: false
  };

  // Temporary state for edit mode
  public tempState: SortState = {
    primarySort: { column: '', direction: 'asc' },
    secondarySort: null,
    showSecondarySort: false
  };

  // Debounced emit function for immediate changes in view mode
  private debouncedEmit = debounce(() => this.emitSortChange(), 300);

  // Component readiness check
  public get isComponentReady(): boolean {
    return !!(this.sortableColumns && this.sortableColumns.length > 0);
  }

  // Reactive column name getters that update when sortableColumns changes
  public get primaryColumnName(): string {
    if (!this.currentState.primarySort.column) return '';
    return getColumnDisplayName(this.currentState.primarySort.column, this.sortableColumns);
  }

  public get secondaryColumnName(): string {
    if (!this.currentState.secondarySort?.column) return '';
    return getColumnDisplayName(this.currentState.secondarySort.column, this.sortableColumns);
  }

  // View mode display data
  public get viewModeDisplay(): ViewModeDisplay {
    if (!this.isComponentReady) {
      return {
        sortSummaryText: 'Loading...',
        activeSorts: [],
        hasAnySorting: false,
      };
    }

    // Create a reactive version that uses computed column names
    const activeSorts: SortOption[] = [];

    if (this.currentState.primarySort.column) {
      activeSorts.push(this.currentState.primarySort);
    }

    if (this.currentState.secondarySort?.column) {
      activeSorts.push(this.currentState.secondarySort);
    }

    // Generate summary text using reactive column names
    let sortSummaryText = 'No sorting';
    if (activeSorts.length > 0) {
      const summaryParts: string[] = [];

      if (this.currentState.primarySort.column) {
        const direction = this.currentState.primarySort.direction === 'asc' ? '↑' : '↓';
        summaryParts.push(`${this.primaryColumnName} ${direction}`);
      }

      if (this.currentState.secondarySort?.column) {
        const direction = this.currentState.secondarySort.direction === 'asc' ? '↑' : '↓';
        summaryParts.push(`${this.secondaryColumnName} ${direction}`);
      }

      sortSummaryText = summaryParts.join(' ');
    }

    return {
      sortSummaryText,
      activeSorts,
      hasAnySorting: activeSorts.length > 0,
    };
  }

  // Edit mode computed properties for v-btn-toggle indices
  public get tempPrimaryDirectionIndex(): string {
    return this.tempState.primarySort.direction === 'asc' ? '0' : '1';
  }

  public set tempPrimaryDirectionIndex(value: string) {
    this.tempState.primarySort.direction = value === '0' ? 'asc' : 'desc';
    this.hasUnsavedChanges = true;
  }

  public get tempSecondaryDirectionIndex(): string {
    if (!this.tempState.secondarySort) return '0';
    return this.tempState.secondarySort.direction === 'asc' ? '0' : '1';
  }

  public set tempSecondaryDirectionIndex(value: string) {
    if (this.tempState.secondarySort) {
      this.tempState.secondarySort.direction = value === '0' ? 'asc' : 'desc';
      this.hasUnsavedChanges = true;
    }
  }

  // Available columns for secondary sort in edit mode
  public get tempAvailableSecondaryColumns(): SortableColumn[] {
    if (!this.sortableColumns || this.sortableColumns.length === 0) {
      return [];
    }
    return getAvailableSecondaryColumns(
      this.sortableColumns,
      this.tempState.primarySort.column
    );
  }

  // Computed property for secondary sort column to handle null values
  public get tempSecondarySortColumn(): string {
    return this.tempState.secondarySort?.column || '';
  }

  public set tempSecondarySortColumn(value: string) {
    if (!this.tempState.secondarySort) {
      this.tempState.secondarySort = { column: value, direction: 'asc' };
    } else {
      this.tempState.secondarySort.column = value;
    }
    this.hasUnsavedChanges = true;
  }

  // Edit mode state flags
  public get tempCanAddSecondarySort(): boolean {
    return !this.tempState.showSecondarySort &&
           !!this.tempState.primarySort.column &&
           this.tempAvailableSecondaryColumns.length > 0;
  }

  public get tempHasAnySorting(): boolean {
    return hasActiveSorting(
      this.tempState.primarySort,
      this.tempState.secondarySort
    );
  }

  // Lifecycle hooks
  public mounted() {
    this.initializeFromProps();
  }

  // Initialize component state from props
  @Watch('sortableColumns', { immediate: true })
  private onSortableColumnsChanged() {
    if (this.sortableColumns && this.sortableColumns.length > 0) {
      this.initializeFromProps();
    }
  }

  @Watch('initialSortBy', { immediate: true })
  private onInitialSortByChanged() {
    if (this.sortableColumns && this.sortableColumns.length > 0) {
      this.initializeFromProps();
    }
  }

  @Watch('initialSortDesc', { immediate: true })
  private onInitialSortDescChanged() {
    if (this.sortableColumns && this.sortableColumns.length > 0) {
      this.initializeFromProps();
    }
  }

  private initializeFromProps() {
    if (!this.sortableColumns || this.sortableColumns.length === 0) {
      return;
    }

    const { primary, secondary } = convertArraysToSortOptions(
      this.initialSortBy || [],
      this.initialSortDesc || [],
      this.sortableColumns,
    );

    // Only update if we have valid sort data or if current state is empty
    if (primary.column || !this.currentState.primarySort.column) {
      this.currentState.primarySort = primary;
      this.currentState.secondarySort = secondary;
      this.currentState.showSecondarySort = !!secondary;
    }
  }

  // Get column display name
  public getColumnName = (columnCode: string): string => {
    // Safety check - should not be called when component is not ready
    if (!this.isComponentReady) {
      return cleanColumnName(columnCode);
    }
    return getColumnDisplayName(columnCode, this.sortableColumns);
  };

  // Mode switching methods
  public enterEditMode() {
    this.isEditMode = true;
    this.tempState = createTempSortState(
      this.currentState.primarySort,
      this.currentState.secondarySort
    );
    this.hasUnsavedChanges = false;
  }

  public exitEditMode() {
    this.isEditMode = false;
    this.hasUnsavedChanges = false;
  }

  public applyEdit() {
    // Apply temp state to current state
    this.currentState.primarySort = { ...this.tempState.primarySort };
    this.currentState.secondarySort = this.tempState.secondarySort ?
      { ...this.tempState.secondarySort } : null;
    this.currentState.showSecondarySort = this.tempState.showSecondarySort;

    this.exitEditMode();
    this.emitSortChange();
  }

  public cancelEdit() {
    this.exitEditMode();
  }

  // Quick direction toggle in view mode
  public quickToggleDirection(sortIndex: number) {
    if (sortIndex === 0 && this.currentState.primarySort.column) {
      this.currentState.primarySort.direction = toggleSortDirection(
        this.currentState.primarySort.direction
      );
    } else if (sortIndex === 1 && this.currentState.secondarySort) {
      this.currentState.secondarySort.direction = toggleSortDirection(
        this.currentState.secondarySort.direction
      );
    }

    this.debouncedEmit();
  }

  // Edit mode event handlers
  public onTempPrimarySortColumnChange() {
    if (!this.tempState.primarySort.column) {
      this.clearTempSorting();
      return;
    }

    // Set default direction based on column data type
    const column = this.sortableColumns.find(
      col => col.code === this.tempState.primarySort.column
    );
    if (column) {
      this.tempState.primarySort.direction = getDefaultDirection(column.dataType);
    }

    // If secondary sort uses the same column, clear it
    if (this.tempState.secondarySort &&
        this.tempState.secondarySort.column === this.tempState.primarySort.column) {
      this.removeTempSecondarySort();
    }

    this.hasUnsavedChanges = true;
  }

  public onTempPrimarySortDirectionChange() {
    this.hasUnsavedChanges = true;
  }

  public onTempSecondarySortColumnChange() {
    if (!this.tempState.secondarySort?.column) {
      this.removeTempSecondarySort();
      return;
    }

    // Set default direction based on column data type
    const column = this.sortableColumns.find(
      col => col.code === this.tempState.secondarySort?.column
    );
    if (column && this.tempState.secondarySort) {
      this.tempState.secondarySort.direction = getDefaultDirection(column.dataType);
    }

    this.hasUnsavedChanges = true;
  }

  public onTempSecondarySortDirectionChange() {
    this.hasUnsavedChanges = true;
  }

  public addTempSecondarySort() {
    if (this.tempAvailableSecondaryColumns.length > 0) {
      const firstAvailable = this.tempAvailableSecondaryColumns[0];
      this.tempState.secondarySort = {
        column: firstAvailable.code,
        direction: getDefaultDirection(firstAvailable.dataType)
      };
      this.tempState.showSecondarySort = true;
      this.hasUnsavedChanges = true;
    }
  }

  public removeTempSecondarySort() {
    this.tempState.secondarySort = null;
    this.tempState.showSecondarySort = false;
    this.hasUnsavedChanges = true;
  }

  public clearTempSorting() {
    const emptyState = getEmptySortState();
    this.tempState.primarySort = emptyState.primarySort;
    this.tempState.secondarySort = emptyState.secondarySort;
    this.tempState.showSecondarySort = emptyState.showSecondarySort;
    this.hasUnsavedChanges = true;
  }

  // Emit sort change event to parent
  private emitSortChange() {
    const { sortBy, sortDesc } = convertSortOptionsToArrays(
      this.currentState.primarySort,
      this.currentState.secondarySort
    );

    const event: SortChangeEvent = { sortBy, sortDesc };
    this.$emit('sort-changed', event);
  }
}
